import { useEffect, useMemo, useState } from "react";
import { AtSign, Save } from "lucide-react";

import { useStore } from "../context/StoreContext";
import { PageHeader } from "../components/bits";
import { PlatformIcon } from "../components/branding";
import { INPUT, PrimaryBtn, Toggle } from "../components/ui";
import { PLATFORMS } from "../lib/platforms";

export default function AccountsPage() {
  const { accounts, setAccount, showToast } = useStore();
  const connectedCount = accounts.filter((a) => a.connected).length;

  // Local draft of usernames so edits are explicit and saved on click.
  const initial = useMemo(() => {
    const m = {};
    accounts.forEach((a) => (m[a.platform] = a.handle || ""));
    return m;
  }, [accounts]);
  const [names, setNames] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setNames(initial);
  }, [initial]);

  const dirty = accounts.some((a) => (names[a.platform] || "") !== (a.handle || ""));

  const saveNames = async () => {
    setSaving(true);
    try {
      const changed = accounts.filter((a) => (names[a.platform] || "") !== (a.handle || ""));
      for (const a of changed) {
        await setAccount(a.platform, { handle: (names[a.platform] || "").replace(/^@/, "").trim() });
      }
      showToast("Usernames saved");
    } catch {
      showToast("Could not save usernames");
    }
    setSaving(false);
  };

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Social accounts"
        subtitle={`Pick the networks to generate for and set a username for each. ${connectedCount} connected.`}
        right={
          <PrimaryBtn onClick={saveNames} disabled={!dirty || saving}>
            <Save size={16} />
            Save
          </PrimaryBtn>
        }
      />

      <div className="grid sm:grid-cols-2 gap-3.5">
        {accounts.map((acc) => {
          const meta = PLATFORMS[acc.platform];
          return (
            <div key={acc.platform} className="bg-surface border border-border rounded-2xl p-4">
              <div className="flex items-center gap-3.5">
                <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 bg-foreground/[0.06] text-foreground">
                  <PlatformIcon id={acc.platform} size={21} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-foreground text-[15px]">{meta.name}</div>
                  <div className="text-[12.5px] text-faint">
                    {acc.connected ? "Generating posts" : "Not generating"}
                  </div>
                </div>
                <Toggle
                  checked={acc.connected}
                  onChange={(v) => setAccount(acc.platform, { connected: v })}
                />
              </div>

              {acc.connected ? (
                <div className="mt-3.5 relative">
                  <AtSign
                    size={15}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-faint pointer-events-none"
                  />
                  <input
                    className={`${INPUT} pl-8`}
                    placeholder="username"
                    value={names[acc.platform] || ""}
                    onChange={(e) =>
                      setNames((n) => ({ ...n, [acc.platform]: e.target.value }))
                    }
                  />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}